import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CreateRoleForRequestFormTitle from "./create-role-for-request-form-header";
import CreateRoleForRequestForm from "./create-role-for-request-form";
import { Box } from "@mui/material";
import { Button } from "../../../common/buttons";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { addRolesToRequest } from "../../../../reducers/administration/requestSlice";
import { nanoid } from "@reduxjs/toolkit";
import { ErrorsDialog } from "../../../common/dialogs";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { requestsService } from "../../../../services/requests-service";
import { RequestRole, CreateRoleForRequestFormState, GetRoleHeaderResponse } from "@/types/brm";
import { ResponseError } from "../../../../types/common/responses/responses-types";

const validateForm = (form: CreateRoleForRequestFormState, intl: IntlShape) => {
    const validations = {
        id: [(v: string) => /^[ZYzy]/.test(v) || intl.formatMessage({ id: "create-role-for-request-form.error.input-role-name" })],
        groupId: [(v: string) => v.trim() !== "" || intl.formatMessage({ id: "create-role-for-request-form.error.empty-field" })],
        roleOwner: [(v: { key: string; text: string }[]) => v.length > 0 || intl.formatMessage({ id: "create-role-for-request-form.error.empty-field" })],
        deputyRoleOwner: [(v: { key: string; text: string }[]) => v.length > 0 || intl.formatMessage({ id: "create-role-for-request-form.error.empty-field" })],
    };

    const errors = Object.fromEntries(
        Object.entries(validations)
            .map(([key, rules]) => {
                const value = form[key as keyof CreateRoleForRequestFormState];
                const message = rules.map((rule) => rule(value)).find((res) => typeof res === "string");
                return message ? [key, { value, message }] : null;
            })
            .filter(Boolean) as [string, { value: { key: string; text: string }[] | string; message: string }][]
    );

    const roleOwnerKeys = form.roleOwner.map((o) => o.key);
    const deputyOwnerKeys = form.deputyRoleOwner.map((o) => o.key);

    const duplicates = roleOwnerKeys.filter((key) => deputyOwnerKeys.includes(key));

    if (duplicates.length > 0) {
        errors["roleOwner"] = {
            value: form.roleOwner,
            message: intl.formatMessage({ id: "create-role-for-request-form.error.input-role-owner-role" }),
        };
        errors["deputyRoleOwner"] = {
            value: form.deputyRoleOwner,
            message: intl.formatMessage({ id: "create-role-for-request-form.error.input-deputy-role-owner" }),
        };
    }

    return errors;
};

const CreateRoleForRequestFormPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();
    const currentRequest = useSelector((state: RootState) => state.administration.requests.currentRequest);
    const rolesForView = useSelector((state: RootState) => state.administration.requests.rolesForView);

    useEffect(() => {
        if (!currentRequest) {
            navigate("..", { relative: "path" });
        }
    }, [navigate, currentRequest]);

    const [form, setForm] = useState<CreateRoleForRequestFormState>({
        id: "",
        description: "",
        groupId: currentRequest?.groupId ?? "",
        isComplex: false,
        profileName: "",
        profileDescription: "",
        roleOwner: [],
        roleOwnerFullname: "",
        deputyRoleOwner: [],
        deputyRoleOwnerFullname: "",
    });

    const [validationErrors, setValidationErrors] = useState<Record<string, { message: string; value: { key: string; text: string }[] | string }> | null>(null);
    const [localError, setLocalError] = useState<ResponseError | null>(null);

    const handleBackClick = useCallback(() => {
        navigate("..", { relative: "path" });
    }, [navigate]);

    useEffect(() => {
        setValidationErrors((prevErrors) => {
            if (!prevErrors) return null;
            const newErrors = { ...prevErrors };

            const hasDuplicates = form.roleOwner.some((owner) => form.deputyRoleOwner.some((deputy) => deputy.key === owner.key));

            if (hasDuplicates) {
                delete newErrors.roleOwner;
                delete newErrors.deputyRoleOwner;
            } else {
                if (form.roleOwner.length > 0) {
                    delete newErrors.roleOwner;
                }
                if (form.deputyRoleOwner.length > 0) {
                    delete newErrors.deputyRoleOwner;
                }
            }

            return newErrors;
        });
    }, [form.roleOwner, form.deputyRoleOwner]);

    const handleSubmit = async () => {
        const errors = validateForm(form, intl);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);

            const responseError: ResponseError = {
                code: "VALIDATION_ERROR",
                type: "error",
                title: intl.formatMessage({ id: "common.validation-error" }),
                detail: intl.formatMessage({ id: "common.validation-error.invalid-data" }),
                errors: Object.keys(errors).map((field) => ({
                    field,
                    message: errors[field].message,
                    additional_info: "",
                    code: "",
                    type: "",
                    value: "",
                })),
            };

            setLocalError(responseError);

            return;
        }

        let systemId: string | null = null;
        let roleHeader: GetRoleHeaderResponse | null = null;

        try {
            systemId = await requestsService.getTargetSystemId(form.groupId);

            roleHeader = await requestsService.getRoleHeader(form.id, systemId);
        } catch (error) {
            setLocalError(error as ResponseError);

            return;
        }

        if (roleHeader !== null) {
            const validationRoleError = {
                value: form.id,
                message: intl.formatMessage({ id: "create-role-for-request-form.error.input-role-existed" }),
            };

            setValidationErrors({
                id: validationRoleError,
            });

            return;
        }

        const [owner] = form.roleOwner;
        const [deputyOwner] = form.deputyRoleOwner;

        const createdRole: RequestRole = {
            id: nanoid(),
            isNew: true,
            description: form.description,
            role: form.id.toUpperCase(),
            groupId: form.groupId,
            isComplex: form.isComplex,
            children: [],
            systemId: systemId,
            owner: {
                username: owner?.key ?? "",
                fullName: owner?.text ?? "",
            },
            deputyOwner: {
                username: deputyOwner?.key ?? "",
                fullName: deputyOwner?.text ?? "",
            },
            added: true,
        };

        const existingRoleNames = new Set(rolesForView.roles.map((role) => role.role));
        const isDuplicateRole = existingRoleNames.has(createdRole.role);

        if (isDuplicateRole) {
            setLocalError({
                code: "VALIDATION_ERROR",
                type: "error",
                title: intl.formatMessage({ id: "common.validation-error" }),
                detail: intl.formatMessage({ id: "create-role-for-request-form.error.input-role-name-duplicate" }),
                errors: [],
            });
            return;
        }

        dispatch(
            addRolesToRequest({
                roles: [createdRole],
                groupId: form.groupId,
            })
        );
        handleBackClick();
    };

    return (
        <>
            <Box sx={{ backgroundColor: "#f5f5f5", padding: "15px 20px 15px" }}>
                <CreateRoleForRequestFormTitle onBackClick={handleBackClick} />
                <CreateRoleForRequestForm
                    form={form}
                    setForm={(newData) => setForm((prev) => ({ ...prev, ...newData }))}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    groupIdDisabled={!!currentRequest?.groupId?.length}
                />
            </Box>
            <Box className="create-role-for-request-form-buttons">
                <Button variant="contained" className="create-role-for-request-form-button-contained" onClick={handleSubmit}>
                    <FormattedMessage id="create-role-for-request-form.btn-save" />
                </Button>
                <Button variant="outlined" className="create-role-for-request-form-button-outlined" onClick={handleBackClick}>
                    <FormattedMessage id="create-role-for-request-form.btn-cancel" />
                </Button>
            </Box>
            <ErrorsDialog error={localError} open={localError?.type === "error"} onClose={() => setLocalError(null)} />
        </>
    );
};

export default CreateRoleForRequestFormPage;
